import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { XMarkIcon, PlusIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-toastify'
import { Button } from 'src/components/button'
import { Input } from 'src/components/input'
import { propertyApi } from 'src/services/properties/api'
import { propertyQueries } from 'src/services/properties/queries'
import type { Property } from 'src/services/properties/models'

type InboundCalendarsCardProps = {
  property: Property
}

/**
 * Card component for managing inbound iCal subscription URLs on a property.
 *
 * Lets users add, edit, and remove feed URLs. Saving PATCHes the property's
 * `inboundCalendarUrls` field. A separate "Sync now" button triggers an
 * on-demand calendar sync.
 */
export const InboundCalendarsCard = ({ property }: InboundCalendarsCardProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const serverUrls = useMemo(
    () => property.inboundCalendarUrls ?? [],
    [property.inboundCalendarUrls],
  )
  const [urls, setUrls] = useState<string[]>(serverUrls)
  const [errors, setErrors] = useState<Record<number, string>>({})

  // Re-sync local state when the server state changes (e.g. after save + refetch).
  useEffect(() => {
    setUrls(serverUrls)
  }, [serverUrls])

  const isDirty = useMemo(() => {
    if (urls.length !== serverUrls.length) return true
    return urls.some((u, i) => u !== serverUrls[i])
  }, [urls, serverUrls])

  const handleUrlChange = useCallback((index: number, value: string) => {
    setUrls((prev) => prev.map((u, i) => (i === index ? value : u)))
    setErrors((prev) => {
      const next = { ...prev }
      delete next[index]
      return next
    })
  }, [])

  const handleRemove = useCallback((index: number) => {
    setUrls((prev) => prev.filter((_, i) => i !== index))
    setErrors((prev) => {
      const next: Record<number, string> = {}
      for (const [k, v] of Object.entries(prev)) {
        const ki = Number(k)
        if (ki < index) next[ki] = v
        else if (ki > index) next[ki - 1] = v
      }
      return next
    })
  }, [])

  const handleAdd = useCallback(() => {
    setUrls((prev) => [...prev, ''])
  }, [])

  // Validate client-side: each non-empty URL must start with https://
  const validateUrls = useCallback((): boolean => {
    const newErrors: Record<number, string> = {}
    let valid = true
    for (let i = 0; i < urls.length; i++) {
      const url = urls[i].trim()
      if (!url) {
        newErrors[i] = t('calendarFeeds.urlEmpty')
        valid = false
      } else if (!url.startsWith('https://')) {
        newErrors[i] = t('calendarFeeds.urlHttps')
        valid = false
      }
    }
    setErrors(newErrors)
    return valid
  }, [urls, t])

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: () => {
      const trimmedUrls = urls.map((u) => u.trim()).filter(Boolean)
      return propertyApi.update({ id: property.id, inboundCalendarUrls: trimmedUrls } as never)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...propertyQueries.all(), property.id] })
      toast.success(t('calendarFeeds.savedToast'))
      setErrors({})
    },
    onError: (error: unknown) => {
      if (isAxiosError(error) && error.response?.status === 400 && error.response.data) {
        const data = error.response.data as Record<string, string[] | string>
        const serverErrors = data.inbound_calendar_urls
        if (serverErrors && Array.isArray(serverErrors)) {
          const mapped: Record<number, string> = {}
          serverErrors.forEach((msg, idx) => {
            if (msg) mapped[idx] = msg
          })
          setErrors(mapped)
          return
        }
      }
      toast.error(t('calendarFeeds.saveError'))
    },
  })

  const handleSave = useCallback(() => {
    if (!validateUrls()) return
    saveMutation.mutate()
  }, [validateUrls, saveMutation])

  // Sync now mutation
  // TODO: Replace with async-job-tracker progress tracking when that spec group ships.
  const syncMutation = useMutation({
    mutationFn: () => propertyApi.csc.syncCalendars(property.id),
    onMutate: () => {
      toast.info(t('calendarFeeds.syncingToast'))
    },
  })

  const handleSync = useCallback(() => {
    syncMutation.mutate()
  }, [syncMutation])

  const nonEmptyUrls = urls.filter((u) => u.trim().length > 0)

  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <div className="mb-1">
        <h4 className="text-sm font-medium text-text-primary">{t('calendarFeeds.title')}</h4>
        <p className="mt-0.5 text-xs text-text-placeholder">{t('calendarFeeds.description')}</p>
      </div>

      <div className="mt-3 flex flex-col gap-2">
        {urls.map((url, index) => (
          <div key={index}>
            <div className="flex items-center gap-2">
              <Input
                value={url}
                placeholder="https://www.airbnb.com/calendar/ical/..."
                onChange={(e) => handleUrlChange(index, e.target.value)}
                className="flex-1 font-mono text-xs"
              />
              <Button
                variant="ghost"
                iconOnly
                shape="square"
                type="button"
                onClick={() => handleRemove(index)}
                className="shrink-0"
                aria-label={t('calendarFeeds.removeUrl')}
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </Button>
            </div>
            {errors[index] && <p className="mt-1 text-xs text-error">{errors[index]}</p>}
          </div>
        ))}
      </div>

      <div className="mt-3">
        <Button type="button" variant="ghost" onClick={handleAdd} className="text-sm">
          <PlusIcon className="mr-1 h-4 w-4" aria-hidden="true" />
          {t('calendarFeeds.addFeedUrl')}
        </Button>
      </div>

      {urls.length === 0 && (
        <div className="mt-3 rounded-md bg-surface-hover p-3">
          <p className="text-sm text-text-secondary">{t('calendarFeeds.noFeeds')}</p>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <Button
          type="button"
          variant="primary"
          onClick={handleSave}
          disabled={!isDirty || saveMutation.isPending}
          className="text-sm"
        >
          {saveMutation.isPending ? t('calendarFeeds.savingBtn') : t('calendarFeeds.saveBtn')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={handleSync}
          disabled={syncMutation.isPending || nonEmptyUrls.length === 0}
          className="text-sm"
          isLoading={syncMutation.isPending}
        >
          <ArrowPathIcon className="mr-1 h-4 w-4" aria-hidden="true" />

          {syncMutation.isPending ? t('calendarFeeds.syncingBtn') : t('calendarFeeds.syncNow')}
        </Button>
      </div>
    </div>
  )
}
